// Pricing editor slice: per-row ephemeral UI state for the admin pricing
// matrix page. The matrix renders rates straight from `adminCatalog.data.pricing`
// (the authoritative server data); this slice only tracks what the user has
// typed but not yet saved, the in-flight save status, and the latest error per
// row. Cleared on page unmount via `resetEditor`.
//
// Row shape:
//   { dirty: { [tierIndex]: string }, status, error, savedAt, queued }
//   - dirty values stay as raw strings so a cell can round-trip empty/partial
//     input without us parsing too eagerly.
//   - status: 'idle' | 'saving' | 'saved' | 'error'
//   - queued: another save was requested while one was in flight — re-fire on resolve.
import { createSlice } from '@reduxjs/toolkit';
import { savePricing } from './admin-catalog-slice.js';
import { AREA_BUCKETS } from '../config/pricing-buckets.js';
import { PRICING_STRATEGIES } from '../config/pricing-strategies.js';

const emptyRow = () => ({
  dirty: {},
  status: 'idle',
  error: null,
  savedAt: null,
  queued: false,
  // Tier indices touched by the in-flight or just-completed save. Used so the
  // cell-level visual marker (saving spinner, saved flash, error tint) only
  // lights up the cells that were actually included in the save.
  inFlightTiers: [],
});

const ensureRow = (state, rowKey) => {
  if (!state.rows[rowKey]) state.rows[rowKey] = emptyRow();
  return state.rows[rowKey];
};

const pricingEditorSlice = createSlice({
  name: 'pricingEditor',
  initialState: { rows: {} },
  reducers: {
    setCellValue: (state, { payload }) => {
      const {
        rowKey, tierIndex, value, savedRate,
      } = payload;
      const row = ensureRow(state, rowKey);
      // If the typed value parses back to the saved rate, drop the dirty entry
      // — the user reverted by hand and the row should stop counting as unsaved.
      // An empty string never counts as "matches saved": otherwise clearing a
      // saved-0 cell would coerce to 0, drop the draft, and snap "0" back into
      // the box, making it impossible to delete the 0 before typing a new rate.
      const parsed = value === '' ? 0 : Number(value);
      const matchesSaved = value !== ''
        && Number.isFinite(parsed) && parsed === Number(savedRate ?? 0);
      if (matchesSaved) {
        delete row.dirty[tierIndex];
      } else {
        row.dirty[tierIndex] = value;
      }
      if (row.status === 'error') {
        row.status = 'idle';
        row.error = null;
      }
      // Once a row has no pending changes and isn't mid-save, drop the entry
      // so selectSaveStatusCounts doesn't keep counting a "saved" flash row.
      if (Object.keys(row.dirty).length === 0 && row.status !== 'saving') {
        delete state.rows[rowKey];
      }
    },
    revertRow: (state, { payload }) => {
      delete state.rows[payload.rowKey];
    },
    resetEditor: (state) => { state.rows = {}; },
    saveStarted: (state, { payload }) => {
      const row = ensureRow(state, payload.rowKey);
      row.status = 'saving';
      row.error = null;
      row.inFlightTiers = payload.tierIndices || [];
    },
    saveSucceeded: (state, { payload }) => {
      const row = ensureRow(state, payload.rowKey);
      row.dirty = {};
      row.status = 'saved';
      row.error = null;
      row.savedAt = Date.now();
    },
    saveFailed: (state, { payload }) => {
      const row = ensureRow(state, payload.rowKey);
      row.status = 'error';
      row.error = payload.error;
    },
    markQueued: (state, { payload }) => {
      const row = ensureRow(state, payload.rowKey);
      row.queued = true;
    },
    clearQueued: (state, { payload }) => {
      const row = state.rows[payload.rowKey];
      if (row) row.queued = false;
    },
    // Clear the "saved" flash on a row, but only if it's still idle-saved
    // (no new edits have started since). Scheduled by saveRow on success.
    clearSavedFlash: (state, { payload }) => {
      const row = state.rows[payload.rowKey];
      if (!row) return;
      if (row.status !== 'saved') return;
      if (Object.keys(row.dirty).length > 0) return;
      delete state.rows[payload.rowKey];
    },
  },
});

export const {
  setCellValue, revertRow, resetEditor,
} = pricingEditorSlice.actions;

const {
  saveStarted, saveSucceeded, saveFailed, markQueued, clearQueued, clearSavedFlash,
} = pricingEditorSlice.actions;

const SAVED_FLASH_MS = 250;

// Empty -> 0 (a blank cell clears the rate). Non-numeric -> NaN (validated below).
const parseRate = (value) => {
  if (value == null || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
};

// Selectors —————————————————————————————————————————————————————————————

export const selectRowState = (rowKey) => (s) => s.pricingEditor.rows[rowKey] || null;

// The cell reads the dirty value if any, otherwise the saved rate.
export const selectCellValue = (rowKey, tierIndex, savedRate) => (s) => {
  const row = s.pricingEditor.rows[rowKey];
  if (row && Object.hasOwn(row.dirty, tierIndex)) {
    return row.dirty[tierIndex];
  }
  return String(savedRate ?? 0);
};

export const selectIsCellDirty = (rowKey, tierIndex, savedRate) => (s) => {
  const row = s.pricingEditor.rows[rowKey];
  if (!row || !Object.hasOwn(row.dirty, tierIndex)) return false;
  const parsed = parseRate(row.dirty[tierIndex]);
  return !Number.isFinite(parsed) || parsed !== Number(savedRate ?? 0);
};

// Aggregate counts for the global SaveStatusPill.
export const selectSaveStatusCounts = (s) => {
  const counts = { dirty: 0, saving: 0, error: 0 };
  Object.values(s.pricingEditor.rows).forEach((row) => {
    if (row.status === 'saving') counts.saving += 1;
    else if (row.status === 'error') counts.error += 1;
    else if (Object.keys(row.dirty).length > 0) counts.dirty += 1;
  });
  return counts;
};

// Save thunk —————————————————————————————————————————————————————————————

// Build the 4-tier array for a row by overlaying its dirty values onto the
// last-saved rates. Pure so the thunk and tests can share it.
export const buildTiersForSave = (savedTiers, dirty) => AREA_BUCKETS.map((bucket, i) => {
  const dirtyHas = Object.hasOwn(dirty || {}, i);
  const rate = dirtyHas ? parseRate(dirty[i]) : Number(savedTiers?.[i]?.rate ?? 0);
  return { ...bucket, rate };
});

// Save the row. Coalesces concurrent saves: if a save is already in flight,
// mark `queued` and re-fire (with the latest dirty values) once it resolves.
// `strategy` selects how the row's value cells map to a pricing payload:
//   - tiered: `savedTiers` is the row's current 4-bucket array; dirty values
//     overlay onto it to compose the full payload PUT /api/admin/pricing wants.
//   - flat: a single cell (index 0); `savedAmount` is the current flat price and
//     a dirty value at index 0 overrides it.
export const saveRow = ({
  rowKey, target, strategy = PRICING_STRATEGIES.TIERED, savedTiers, savedAmount = 0,
}) => async (dispatch, getState) => {
  const beforeRow = getState().pricingEditor.rows[rowKey];
  if (beforeRow?.status === 'saving') {
    dispatch(markQueued({ rowKey }));
    return { ok: true, queued: true };
  }

  const dirty = beforeRow?.dirty || {};
  const dirtyIndices = Object.keys(dirty).map(Number);
  const isFlat = strategy === PRICING_STRATEGIES.FLAT;

  let savePayload;
  if (isFlat) {
    const amount = Object.hasOwn(dirty, 0) ? parseRate(dirty[0]) : Number(savedAmount ?? 0);
    if (!Number.isFinite(amount) || amount < 0) {
      dispatch(saveFailed({ rowKey, error: 'Price must be a non-negative number.' }));
      return { ok: false };
    }
    savePayload = { target, strategy, amount };
  } else {
    const tiers = buildTiersForSave(savedTiers, dirty);
    if (tiers.some((t) => !Number.isFinite(t.rate) || t.rate < 0)) {
      dispatch(saveFailed({ rowKey, error: 'Rates must be non-negative numbers.' }));
      return { ok: false };
    }
    savePayload = { target, strategy, tiers };
  }

  dispatch(saveStarted({ rowKey, tierIndices: dirtyIndices }));
  const result = await dispatch(savePricing(savePayload));

  if (!savePricing.fulfilled.match(result)) {
    dispatch(saveFailed({ rowKey, error: result.payload || 'Could not save.' }));
    return { ok: false };
  }

  dispatch(saveSucceeded({ rowKey }));

  // If new edits landed while saving, re-fire with the freshly-persisted values.
  const afterRow = getState().pricingEditor.rows[rowKey];
  if (afterRow?.queued) {
    dispatch(clearQueued({ rowKey }));
    return dispatch(saveRow({
      rowKey,
      target,
      strategy,
      savedTiers: isFlat ? savedTiers : savePayload.tiers,
      savedAmount: isFlat ? savePayload.amount : savedAmount,
    }));
  }
  // Fade the success marker after a short flash. The reducer guards against
  // wiping a row that's been touched again in the meantime.
  setTimeout(() => dispatch(clearSavedFlash({ rowKey })), SAVED_FLASH_MS);
  return { ok: true };
};

// Save every dirty row sequentially. Called from the explicit "Save changes"
// button. `rows` is the matrix's flat row list so we can resolve each dirty
// rowKey to its `target`, `strategy`, and current saved values without the
// slice needing to mirror catalog data.
export const saveAllDirty = (rows) => async (dispatch, getState) => {
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const dirtyTargets = Object.entries(getState().pricingEditor.rows)
    .filter(([, row]) => Object.keys(row.dirty || {}).length > 0
      && row.status !== 'saving')
    .map(([key]) => byKey.get(key))
    .filter((r) => r?.target);
  if (dirtyTargets.length === 0) return { ok: true };

  // Sequential (reduce chain) so the UI's "Saving…" state is meaningful and a
  // partial failure doesn't leave us mid-batch with conflicting writes.
  const results = await dirtyTargets.reduce(async (acc, matrixRow) => {
    const prior = await acc;
    const res = await dispatch(saveRow({
      rowKey: matrixRow.key,
      target: matrixRow.target,
      strategy: matrixRow.strategy,
      savedTiers: matrixRow.tiers,
      savedAmount: matrixRow.amount,
    }));
    return [...prior, res];
  }, Promise.resolve([]));
  return { ok: results.every((r) => r?.ok) };
};

export default pricingEditorSlice.reducer;
